import { classNames, Component, h, signal, tag } from "omi";
import includes from "lodash-es/includes";
import throttle from "lodash-es/throttle";

import { HighlightInteractionEvent } from "@/editor/extensions/highlight/events";
import { useEditorController } from "@/editor/useEditor";
import { Store } from "@/store";
import { tailwind } from "@/tailwind";

import { HighlightColors } from "./models";
import { getColorClass } from "./utils";

export * from "./toolbar";

type Props = {
  group: string;
  color: HighlightColors;
  active?: boolean;
};

@tag("nw-highlight")
export class NWHighlight extends Component<Props> {
  static css = [tailwind, `:host { display: inline; font-family: inherit !important; }`];

  private defaultColor = HighlightColors.YELLOW;
  private color = signal<HighlightColors>(this.defaultColor);
  private isActive = signal<boolean>(this.props.active || false);
  private mouseDownPosition: { x: number; y: number } | null = null;
  private readonly DRAG_THRESHOLD = 5;

  static propTypes = {
    group: String,
    color: String,
    active: Boolean,
  };

  install() {
    if (!Store.features.hasFeature("projects.highlights")) {
      return;
    }

    this.color.value = (this.getAttribute("color") || this.defaultColor) as HighlightColors;
  }

  installed() {
    this.isActive.value = this.props.active || false;
  }

  uninstall() {
    this.mouseDownPosition = null;
    document.removeEventListener("mousedown", this.withinBoundsListener);
  }

  // Used by the editor to set the active state of the highlight based on a group
  public setActive(active: boolean) {
    if (active) {
      document.addEventListener("mousedown", this.withinBoundsListener);
    } else {
      document.removeEventListener("mousedown", this.withinBoundsListener);
    }

    this.isActive.value = active;
  }

  private withinBoundsListener = (event: MouseEvent) => {
    const path = event.composedPath() as HTMLElement[];

    if (path.some(element => element.classList && element.classList.contains("highlight-ensure-active"))) {
      return;
    }

    if (!includes(path, this.rootElement)) {
      useEditorController()?.setActiveHighlightGroup(this.props.group, false);
      this.setActive(false);
    }
  };

  private handleMouseDown = (event: MouseEvent) => {
    this.mouseDownPosition = {
      x: event.clientX,
      y: event.clientY,
    };
  };

  private handleClick = throttle((event: MouseEvent) => {
    event.preventDefault();

    if (hasInPath(event, "nw-note")) {
      event.stopImmediatePropagation();
    }

    // Check if user was dragging (moved more than 5px in any direction)
    if (this.mouseDownPosition) {
      const deltaX = Math.abs(event.clientX - this.mouseDownPosition.x);
      const deltaY = Math.abs(event.clientY - this.mouseDownPosition.y);

      if (deltaX > this.DRAG_THRESHOLD || deltaY > this.DRAG_THRESHOLD) {
        this.mouseDownPosition = null;
        return;
      }
    }

    const highlightSelectedEvent = new CustomEvent(HighlightInteractionEvent.HIGHLIGHT_INTERACTED, {
      bubbles: true,
      cancelable: true,
      detail: {
        group: this.props.group,
      },
    });

    this.dispatchEvent(highlightSelectedEvent);
    this.mouseDownPosition = null;
  }, 200);

  render() {
    if (!Store.features.hasFeature("projects.highlights")) {
      return <slot></slot>;
    }

    const { bg, border } = getColorClass(this.color.value);

    return (
      <mark
        onClick={this.handleClick}
        onMouseDown={this.handleMouseDown}
        class={classNames(
          "box-border group relative py-[2px] cursor-pointer transition-all duration-100 highlight-border",
          bg,
          {
            [border]: this.isActive.value,
            "z-10": this.isActive.value,
          },
        )}
      >
        <slot></slot>
      </mark>
    );
  }
}

function hasInPath(event: Event, tagName: string) {
  const path = event.composedPath();
  return path.some(element => {
    if (element instanceof HTMLElement) {
      return element.tagName.toLowerCase() === tagName;
    }
    return false;
  });
}
